import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PhoneIcon, EnvelopeIcon, MapPinIcon, CheckCircleIcon, PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';
import SlashList from '../components/SlashList';

// phones_extra / emails_extra are stored as a JSON-encoded array; parse
// defensively so a fresh install or empty string just means "none set".
function parseList(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

const MAX_FILES = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default function Contact() {
  const { t } = useLanguage();
  const [settings, setSettings] = useState({ phone: '+63 917-157-7724', email: 'example@sankyomt.ph', address: '' });
  const [searchParams] = useSearchParams();
  // Arriving from a brand's category page (?about=Brand / Category / Sub)
  // starts the message off naming what the visitor was looking at.
  const about = searchParams.get('about');
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', peza_type: '', message: about ? `${t('contact.inquiryAbout')} ${about}\n\n` : '' });
  const [images, setImages] = useState([]); // File[]
  const [fileError, setFileError] = useState(null);
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'

  useEffect(() => {
    API.get('/settings').then(r => setSettings(s => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  // Revoke object URLs on unmount / whenever the selection changes, so
  // previews don't leak memory as the customer adds and removes images.
  useEffect(() => () => images.forEach(f => URL.revokeObjectURL(f.previewUrl)), [images]);

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file after removing it
    setFileError(null);

    const room = MAX_FILES - images.length;
    if (picked.length > room) {
      setFileError(t('contact.tooManyFiles').replace('{max}', MAX_FILES));
    }
    const oversized = picked.some(f => f.size > MAX_FILE_SIZE);
    if (oversized) setFileError(t('contact.fileTooLarge'));

    const accepted = picked
      .filter(f => f.type.startsWith('image/') && f.size <= MAX_FILE_SIZE)
      .slice(0, Math.max(room, 0))
      .map(f => Object.assign(f, { previewUrl: URL.createObjectURL(f) }));

    setImages(list => [...list, ...accepted]);
  };

  const removeImage = (i) => {
    setImages(list => {
      URL.revokeObjectURL(list[i].previewUrl);
      return list.filter((_, idx) => idx !== i);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      images.forEach(f => data.append('images', f));
      await API.post('/contact', data);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', company: '', peza_type: '', message: '' });
      images.forEach(f => URL.revokeObjectURL(f.previewUrl));
      setImages([]);
    } catch {
      setStatus('error');
    }
  };

  const field = (label, key, type = 'text', required = false) => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        required={required}
        className="w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />
    </div>
  );

  return (
    <PageLayout contactCta={false}>
      <SEO title={t('contact.title')} description="Get in touch with Sankyo Machine Tools Philippines Inc. for product enquiries and support." />
      <PageBanner en="CONTACT US" title={t('contact.title')} breadcrumbs={[{ label: t('contact.title') }]} />

      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <p className="text-primary font-btn text-xs font-semibold tracking-[0.3em] uppercase mb-2">{t('contact.eyebrow')}</p>
              <h2 className="section-title mb-4">{t('contact.heading')}</h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('contact.intro')}
              </p>
            </div>

            <div className="space-y-5">
              {[
                { icon: PhoneIcon, label: t('contact.phone'), values: [settings.phone, ...parseList(settings.phones_extra)].filter(Boolean), hrefPrefix: 'tel:' },
                { icon: EnvelopeIcon, label: t('contact.email'), values: [settings.email, ...parseList(settings.emails_extra)].filter(Boolean), hrefPrefix: 'mailto:' },
                { icon: MapPinIcon, label: t('contact.address'), values: [settings.address].filter(Boolean) },
              ].filter(({ values }) => values.length > 0).map(({ icon: Icon, label, values, hrefPrefix }) => (
                <div key={label} className="flex items-start gap-4">
                  <div className="bg-primary/10 p-2.5 shrink-0 rounded-full">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-accent uppercase tracking-wider mb-0.5">{label}</div>
                    <SlashList values={values} hrefPrefix={hrefPrefix} className="text-gray-700 text-sm" linkClassName="hover:text-primary transition-colors" />
                  </div>
                </div>
              ))}
            </div>

            {/* Map embed placeholder */}
            <div className="h-48 bg-primary/5 border border-primary/10 flex items-center justify-center">
              <a
                href="https://maps.app.goo.gl/gPKzE9Xe9KeauAeeA"
                target="_blank" rel="noopener noreferrer"
                className="text-sm text-primary hover:text-accent transition-colors underline"
              >
                {t('contact.viewOnMap')}
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-gray-50 p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-ink mb-6">{t('contact.sendMessage')}</h3>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CheckCircleIcon className="w-14 h-14 text-green-500 mb-4" />
                  <h4 className="font-semibold text-gray-800 text-lg mb-2">{t('contact.successTitle')}</h4>
                  <p className="text-gray-500 text-sm mb-6">{t('contact.successMessage')}</p>
                  <button onClick={() => setStatus(null)} className="btn-outline text-sm">{t('contact.sendAnother')}</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {field(t('contact.fullName'), 'name', 'text', true)}
                    {field(t('contact.emailAddress'), 'email', 'email', true)}
                    {field(t('contact.phoneNumber'), 'phone', 'tel')}
                    {field(t('contact.company'), 'company')}
                  </div>
                  {field(t('contact.pezaType'), 'peza_type', 'text', true)}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                      {t('contact.message')}<span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      required rows={5}
                      className="w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors resize-none" />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                      {t('contact.attachImages')}
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {images.map((f, i) => (
                        <div key={i} className="relative w-16 h-16 shrink-0">
                          <img src={f.previewUrl} alt="" className="w-full h-full object-cover border border-gray-200" />
                          <button type="button" onClick={() => removeImage(i)} aria-label="Remove image"
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-primary-dark text-white rounded-full hover:bg-red-500 transition-colors">
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {images.length < MAX_FILES && (
                        <label className="w-16 h-16 shrink-0 flex flex-col items-center justify-center gap-1 border border-dashed border-gray-300 text-gray-400 hover:border-primary hover:text-primary cursor-pointer transition-colors">
                          <PaperClipIcon className="w-4 h-4" />
                          <span className="text-[10px]">{t('contact.addImage')}</span>
                          <input type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">{t('contact.attachImagesHint')}</p>
                    {fileError && <p className="text-red-500 text-xs mt-1">{fileError}</p>}
                  </div>

                  {status === 'error' && (
                    <p className="text-red-500 text-sm">{t('contact.error')}</p>
                  )}

                  <button type="submit" disabled={status === 'loading'}
                    className="btn-primary w-full sm:w-auto text-sm tracking-widest uppercase disabled:opacity-60">
                    {status === 'loading' ? t('contact.sending') : t('contact.send')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
